import { get } from 'lodash'
import QRCode from 'qrcode'

import { ObjectDataRecord } from '@types'

const getRecordIndex = (element: Element): number | null => {
  const parent = element.closest('[data-print-record-index]')
  if (!parent) return null
  const idx = parent.getAttribute('data-print-record-index')

  return idx !== null ? Number(idx) : null
}

const getFieldValue = (records: ObjectDataRecord[], element: Element): string | null => {
  const idx = getRecordIndex(element)
  if (idx === null) return null

  const record = records[idx]
  if (!record) return null

  const field = element.getAttribute('data-field')
  if (!field) return null

  const value = get(record, field)

  return value !== undefined && value !== null ? String(value) : null
}

const removeSvgDimensionsQr = (svgEl: Element): void => {
  svgEl.removeAttribute('width')
  svgEl.removeAttribute('height')
  const el = svgEl as HTMLElement
  el.style.width = '100%'
  el.style.height = '100%'
  el.style.display = 'block'
}

const appendSvgForQr = (element: Element, svgString: string): void => {
  element.innerHTML = ''
  const parser = new DOMParser()
  const svgDoc = parser.parseFromString(svgString, 'image/svg+xml')
  const svgEl = svgDoc.documentElement
  removeSvgDimensionsQr(svgEl)
  element.appendChild(svgEl)
}

const parsePositiveNumberAttr = (raw: string | null, fallback: number): number => {
  if (raw === null || raw === '') return fallback
  const n = Number(raw)

  return Number.isFinite(n) && n > 0 ? n : fallback
}

/**
 * PDF417 после rotate=R — «длинная» сторона символа по вертикали.
 * Вписываем в ячейку через meet по обеим осям (не принудительный width:100%), чтобы символ занимал максимум площади без неравномерного растягивания.
 */
const appendBarcodeSvg = (element: Element, svgString: string): void => {
  element.innerHTML = ''
  const parser = new DOMParser()
  const svgDoc = parser.parseFromString(svgString, 'image/svg+xml')
  const svgEl = svgDoc.documentElement
  svgEl.removeAttribute('width')
  svgEl.removeAttribute('height')
  svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet')
  const el = svgEl as HTMLElement
  el.style.display = 'block'
  el.style.maxWidth = '100%'
  el.style.maxHeight = '100%'
  el.style.width = 'auto'
  el.style.height = 'auto'
  element.appendChild(svgEl)
}

const injectBarcode = async (element: HTMLElement, value: string): Promise<void> => {
  try {
    const bwipjs = await import('bwip-js')
    const symbology = element.getAttribute('data-symbology') || 'pdf417'
    const scale = parsePositiveNumberAttr(element.getAttribute('data-barcode-scale'), 6)
    const height = parsePositiveNumberAttr(element.getAttribute('data-barcode-height'), 14)
    const svgString = bwipjs.default.toSVG({
      bcid: symbology,
      text: value,
      scale,
      height,
      /** R = 90° по часовой: полосы вдоль высоты этикетки (вертикальный PDF417). */
      rotate: 'R',
    })
    appendBarcodeSvg(element, svgString)
  } catch (err) {
    console.warn('[print-forms] Barcode generation failed:', err)
  }
}

const injectQR = async (element: Element, value: string): Promise<void> => {
  try {
    const svgString = await QRCode.toString(value, { type: 'svg', width: 128, margin: 0 })
    appendSvgForQr(element, svgString)
  } catch {
    console.warn('[print-forms] QR generation failed, value:', value)
  }
}

export const postprocessCodes = async (
  container: HTMLElement,
  records: ObjectDataRecord[]
): Promise<void> => {
  const qrElements = Array.from(container.querySelectorAll('[data-print-qr]'))
  const barcodeElements = Array.from(container.querySelectorAll('[data-print-barcode]'))

  await Promise.all([
    ...qrElements.map(el => {
      const value = getFieldValue(records, el)

      return value ? injectQR(el, value) : Promise.resolve()
    }),
    ...barcodeElements.map(el => {
      const value = getFieldValue(records, el)

      return value ? injectBarcode(el as HTMLElement, value) : Promise.resolve()
    }),
  ])
}
